import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { getPlatformPage, updatePlatformPage, type PlatformPageSummary } from "@/lib/admin-api";

import { PageHistory } from "./page-history";
import styles from "./page-editor.module.css";

/**
 * The platform-static page editor (ADR-025).
 *
 * It mounts `@breakingthelines/editor` the way Studio mounts it for an article
 * — title field, body, `onBodyChange` — minus everything a page has no use
 * for: no featured image, no description, no game blocks, no scheduling, no
 * visibility. Platform pages are always public.
 *
 * # NO COLLABORATION, AND WHAT THAT COSTS
 *
 * Studio runs every editable surface through a Yjs session, because two people
 * editing an article at once is the normal case. Two admins editing the terms
 * page at once is not, and a collaboration session needs an editor-service
 * session keyed to a content id, which a page does not have. So this mounts the
 * editor without `collaboration` and relies on the composition's version token
 * for concurrency: a stale save comes back 409 and the admin reloads.
 *
 * Reopening an authored page seeds the stored document through
 * `BTLEditorConfig.initialBody` (editor 0.69.0; the interface is unchanged
 * through 0.71.0), which the editor applies synchronously before first paint.
 * `neverBlankReason` below is what keeps the hazard that seam closes actually
 * closed.
 */

// Lexical is ~1.5 MB and reaches the bundle through these two. Lazy so the
// admins who never open a page editor never download it; see
// page-editor-canvas.tsx.
const PageEditorCanvas = lazy(() =>
  import("./page-editor-canvas").then((m) => ({ default: m.PageEditorCanvas })),
);

/** True when a Lexical document has something in it worth preserving. */
export function bodyJsonHasContent(bodyJson: string | undefined): boolean {
  if (!bodyJson) return false;
  try {
    const parsed = JSON.parse(bodyJson) as { root?: { children?: unknown[] } };
    const children = parsed.root?.children;
    return Array.isArray(children) && children.length > 0;
  } catch {
    // Unparseable is not empty. Treating it as empty is how it gets overwritten.
    return true;
  }
}

/**
 * The never-blank rule: why this save is refused, or null if it is allowed.
 *
 * `initialBody` closed the hazard that used to make an authored page
 * read-only — the editor now seeds the stored document synchronously before
 * first paint, so an open-then-immediate-save writes back what it opened, not
 * an empty canvas. This is the check that keeps it closed rather than trusting
 * that it is.
 *
 * Two ways an editor holding a page's content can end up empty anyway:
 *
 *   - the stored body is malformed. `$seedInitialBody` reports onError and
 *     falls back to an empty document rather than crashing the render tree,
 *     which is the right call for the editor and exactly the wrong thing to
 *     then save over live legal copy.
 *   - the admin selected everything and deleted it. Legitimate, but not
 *     something to do by accident to the terms page, and typing one character
 *     lifts the block.
 *
 * `editorError` blocks on any Lexical failure, not only a seeding one. That is
 * deliberately broad: on a page whose content is the terms a reader agreed to,
 * "the editor reported a problem" is reason enough not to write what is on
 * screen back over it. Reloading re-seeds and clears it.
 */
export function neverBlankReason({
  loadedWithContent,
  hasBody,
  editorError,
}: {
  loadedWithContent: boolean;
  hasBody: boolean;
  editorError: boolean;
}): string | null {
  if (editorError) {
    return "The editor could not load this page. Saving is blocked until it reloads, so a failed load cannot replace the live copy.";
  }
  if (loadedWithContent && !hasBody) {
    return "This page has content but the editor is empty. Saving is blocked so a blank document cannot replace the live copy.";
  }
  return null;
}

type SaveState = "idle" | "saving" | "saved" | "error";

export function PageEditorPage() {
  const { pageSlug = "" } = useParams();

  const [page, setPage] = useState<PlatformPageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Was there content on this page when we opened it? Fixed at LOAD time, and
  // never recomputed from a save response — it is one half of the never-blank
  // rule below, and a page that gains content mid-session must not start
  // failing that rule against its own new body.
  const [loadedWithContent, setLoadedWithContent] = useState(false);
  // The document handed to the editor, captured at load and never updated.
  // Lexical only reads initialBody when the composer initialises, so feeding it
  // `page.bodyJson` — which the save response replaces — would pass a value
  // that silently does nothing after mount. Freezing it says so.
  const [seedBody, setSeedBody] = useState("");
  // Set when the editor reports it could not build the document it was handed
  // (a malformed stored body seeds as empty rather than crashing) or fails
  // some other way mid-edit.
  const [editorError, setEditorError] = useState(false);

  const [title, setTitle] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  // The latest body lives in a ref so a keystroke does not re-render the page
  // around the editor. `hasBody` is the one thing the render needs from it -
  // whether Save is allowed - and it only flips when emptiness changes, so it
  // costs one render per transition rather than one per keystroke.
  const bodyRef = useRef("");
  const [hasBody, setHasBody] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getPlatformPage(pageSlug)
      .then((data) => {
        if (cancelled) return;
        setPage(data);
        setTitle(data.title);
        bodyRef.current = data.bodyJson ?? "";
        setHasBody(bodyJsonHasContent(bodyRef.current));
        setLoadedWithContent(bodyJsonHasContent(data.bodyJson));
        setSeedBody(data.bodyJson ?? "");
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoadError(err.message ?? "Failed to load page");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pageSlug]);

  // Bumped when a restore replaces the document. It is part of the canvas key,
  // so the composer REMOUNTS and re-reads initialBody. Lexical only reads
  // initialBody when the composer initialises, so nothing short of a remount
  // can put a different document on screen; a state update alone would leave
  // the old text there while every other field said otherwise.
  const [seedGeneration, setSeedGeneration] = useState(0);

  const handleBodyChange = useCallback((json: string) => {
    bodyRef.current = json;
    setHasBody(bodyJsonHasContent(json));
    setSaveState("idle");
  }, []);

  const handleEditorError = useCallback((error: Error) => {
    console.error("[pages] editor error", error);
    setEditorError(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!page) return;
    setSaveState("saving");
    setSaveError(null);

    updatePlatformPage({
      pageSlug: page.slug,
      title,
      bodyJson: bodyRef.current,
      expectedVersion: page.version,
    })
      .then((saved) => {
        setPage(saved);
        setSaveState("saved");
      })
      .catch((err: Error) => {
        setSaveState("error");
        setSaveError(err.message ?? "Failed to save page");
      });
  }, [page, title]);

  /**
   * Reseed the editor from a restore.
   *
   * A restore is the one thing on this screen that changes the document
   * underneath a mounted editor, so it walks the same seam a fresh load does
   * rather than a shortcut of its own: seedBody, then a remount.
   *
   * `hasBody` is set from the SERVER's document, not left to the next
   * onBodyChange. The remounted editor is not obliged to emit one, and until
   * it does, a stale `hasBody` would decide whether Save is allowed. If the
   * remounted editor then fails to seed and emits an empty document, hasBody
   * flips false and the never-blank rule blocks the save, which is the
   * behaviour that matters.
   *
   * `loadedWithContent` is RAISED here and never lowered. Lowering it is what
   * would disarm the never-blank rule on a page that has live copy: the rule
   * fires on "the page has content but the editor is empty", and a false
   * `loadedWithContent` makes an empty editor look legitimate. Raising it is
   * safe in the other direction, and necessary — restoring content into a page
   * that had none must arm the rule from that point on.
   *
   * `editorError` is cleared, because the document that failed is not the
   * document now on screen. If the new one fails too, onEditorError fires
   * again and blocks the save again.
   */
  const handleRestored = useCallback((restored: PlatformPageSummary) => {
    setPage(restored);
    setTitle(restored.title);
    bodyRef.current = restored.bodyJson ?? "";
    setHasBody(bodyJsonHasContent(restored.bodyJson));
    setLoadedWithContent((was) => was || bodyJsonHasContent(restored.bodyJson));
    setSeedBody(restored.bodyJson ?? "");
    setEditorError(false);
    setSaveState("idle");
    setSaveError(null);
    setSeedGeneration((generation) => generation + 1);
  }, []);

  if (loading) return <p className={styles.notice}>Loading page…</p>;
  if (loadError || !page) {
    return <p className={styles.error}>{loadError ?? "Page not found"}</p>;
  }

  const blockedReason = neverBlankReason({ loadedWithContent, hasBody, editorError });
  const canSave = blockedReason === null && title.trim().length > 0 && hasBody;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link className={styles.back} to="/pages">
          Pages
        </Link>
        <div className={styles.headerMeta}>
          <span className={styles.route}>{page.route}</span>
          {!page.isAuthored ? (
            <span className={styles.hardcoded}>
              Not authored. The live route still serves its built-in copy.
            </span>
          ) : null}
        </div>
      </div>

      {blockedReason ? <p className={styles.blocked}>{blockedReason}</p> : null}

      <Suspense fallback={<p className={styles.notice}>Loading editor…</p>}>
        <PageEditorCanvas
          key={`${page.slug}:${seedGeneration}`}
          initialBody={seedBody}
          title={title}
          onTitleChange={setTitle}
          onBodyChange={handleBodyChange}
          onEditorError={handleEditorError}
        />
      </Suspense>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.save}
          disabled={!canSave || saveState === "saving"}
          onClick={handleSave}
        >
          {saveState === "saving" ? "Saving…" : "Save"}
        </button>
        {saveState === "saved" ? <span className={styles.saved}>Saved</span> : null}
        {saveError ? <span className={styles.error}>{saveError}</span> : null}
      </div>

      <PageHistory page={page} onRestored={handleRestored} />
    </div>
  );
}
